/**
 * Lalay Headless Auth — REST client for the WP-side lalay-headless-auth mu-plugin.
 * Endpoints under /wp-json/lalay/v1/*. Tokens are HMAC-signed and stored in
 * localStorage; the user shape includes emailVerified.
 */

const BASE = '/wp-json/lalay/v1';
const TOKEN_KEY = 'lalay:token';
const USER_KEY  = 'lalay:user';

function loadJSON(res) {
  // Some 4xx responses get their body rewritten by nginx's branded error pages.
  // We fall through to a generic shape if the body isn't JSON.
  return res.text().then(t => {
    if (!t) return { success: false };
    try { return JSON.parse(t); } catch { return { success: false }; }
  });
}

async function call(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const tok = getToken();
  if (tok && !headers.Authorization) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  const body = await loadJSON(res);
  return { ok: res.ok, status: res.status, body };
}

export function getToken() { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } }
export function getStoredUser() { try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; } }
export function setSession(token, user) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token); else localStorage.removeItem(TOKEN_KEY);
    if (user)  localStorage.setItem(USER_KEY, JSON.stringify(user)); else localStorage.removeItem(USER_KEY);
  } catch {}
}
export function clearSession() { setSession(null, null); }

export async function registerCustomer({ email, password, firstName, lastName, username }) {
  const { status, body } = await call('/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, firstName, lastName, username }),
  });
  if (status === 200 && body.success) {
    setSession(body.token, body.user);
    return { success: true, user: body.user, token: body.token };
  }
  if (status === 429) return { success: false, message: 'Too many attempts. Wait a few minutes and try again.' };
  return { success: false, message: body.message || 'Registration failed', code: body.code };
}

export async function loginCustomer({ email, username, password }) {
  const id = email || username;
  const { status, body } = await call('/login', {
    method: 'POST',
    body: JSON.stringify({ email: id, password }),
  });
  if (status === 200 && body.success) {
    setSession(body.token, body.user);
    return { success: true, user: body.user, token: body.token };
  }
  if (status === 429) return { success: false, message: 'Too many attempts. Wait a few minutes and try again.' };
  if (status === 401) return { success: false, message: 'Invalid email or password.' };
  return { success: false, message: body.message || 'Login failed' };
}

export async function verifyEmail(token) {
  const { status, body } = await call('/verify-email', { method: 'POST', body: JSON.stringify({ token }) });
  if (status === 200 && body.success) {
    if (body.user) setSession(getToken(), body.user); // refresh user, keep current token
    return { success: true, user: body.user };
  }
  return { success: false, message: body.message || 'Verification failed' };
}

export async function forgotPassword(email) {
  const { status, body } = await call('/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
  // Always treat as success to avoid leaking which emails exist
  return { success: status === 200, message: body.message || 'If an account exists, a reset link has been sent.' };
}

export async function resetPassword({ key, login, password }) {
  const { status, body } = await call('/reset-password', { method: 'POST', body: JSON.stringify({ key, login, password }) });
  if (status === 200 && body.success) {
    setSession(body.token, body.user);
    return { success: true, user: body.user, token: body.token };
  }
  return { success: false, message: body.message || 'Reset failed' };
}

export async function fetchMe() {
  const tok = getToken();
  if (!tok) return null;
  const { status, body } = await call('/me');
  if (status === 200 && body.success) {
    setSession(tok, body.user);
    return body.user;
  }
  // Token invalid/expired — clear session
  clearSession();
  return null;
}

export function logout() { clearSession(); }
